import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Users, Cog, Laptop } from "lucide-react";
import { api, formatAWG, usdApprox } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { usePageSeo } from "@/lib/usePageSeo";
import { ProductCard } from "@/components/cards/ProductCard";
import { PageHero } from "@/components/layout/PageHero";
import { VolumeLaptopInquiryModal } from "@/components/laptops/VolumeLaptopInquiryModal";
import { workstationIconForSlug } from "@/lib/workstationIcons";

export default function LaptopsPage() {
  const [useCases, setUseCases] = useState([]);
  const s = useSettings();
  const [volumeOpen, setVolumeOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // EQUIP Option C: /workstations grid renders 4 use cases + their
    // recommended hardware class. Legacy /api/laptops still exists for
    // historical order-drawer reads (see routes/catalog.py).
    api.get("/use-cases").then((r) => setUseCases(r.data));
  }, []);

  // Deep-link handler for `/workstations#volume-laptops-section` —
  // scrolls the "Equip Your Entire Team" section into view. Reruns
  // whenever `location.hash` changes so clicking the "Teams &
  // Organizations" nav / footer link while ALREADY on /workstations
  // scrolls too (empty-dep effect used to fire only on mount, which
  // silently no-op'd in-page clicks).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.hash !== "#volume-laptops-section") return;
    const t = setTimeout(() => {
      const el = document.getElementById("volume-laptops-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);

  const eyebrow = s.laptops_eyebrow;
  const heading = s.laptops_heading;
  const accent = s.laptops_heading_accent ?? "";
  const description = s.laptops_hero_description;
  const descriptionMobile = s.laptops_hero_description_mobile || "";
  const includesLabel = s.workstations_includes_label || "Includes:";
  const addToCartLabel = s.laptops_page_cta_label;

  // SEO Phase 3 — exact metadata proposed to owner for review.
  usePageSeo({
    title: "Professional Workstations — Ur Designs",
    description: "Purpose-built workstations for creative design, architecture, business and content workflows — configured, tested and delivered by Ur Designs in Aruba.",
    path: "/workstations",
  });

  // Volume Laptop Solutions section — Phase 2 CMS Reconciliation:
  // heading, description and supporting line now live on the settings
  // singleton (see backend/routes/settings.py DEFAULT_SETTINGS +
  // migrate_teams_orgs_copy_sync_v3). Backfill guards ensure legacy
  // documents self-heal on read.
  const vEyebrow = s.volume_laptops_eyebrow;
  const vHeading = s.volume_laptops_heading;
  const vDescription = s.volume_laptops_description;
  const vSupportingLine = s.volume_laptops_supporting_line;
  const vCtaLabel = s.volume_laptops_cta_label;
  // Teams & Organizations hero photograph — Dashboard-editable via
  // Site Settings → Workstations Page. Default set server-side in
  // DEFAULT_SETTINGS so a blank DB still ships a valid image.
  const vImage =
    s.volume_laptops_teams_image ||
    "https://customer-assets-39nsmqrw.emergentagent.net/job_brand-setup-hub/artifacts/b9i1equp_team-workstation-setups.jpg";

  return (
    <div data-testid="laptops-page" className="bg-[#0F0F0F]">
      <PageHero
        testId="laptops-hero"
        eyebrow={eyebrow}
        heading={heading}
        accent={accent}
        accentInline={!!s.laptops_accent_inline}
        showDot={!!s.laptops_show_dot}
        description={description}
        mobileDescription={descriptionMobile}
      />
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 pb-16 sm:pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-[1044px] mx-auto">
          {useCases.length === 0 ? (
            // Subtle skeleton — matches the site's dark treatment so the
            // grid doesn't briefly look empty while /api/use-cases loads.
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid={`use-case-skeleton-${i}`}
                className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] overflow-hidden animate-pulse"
              >
                <div className="aspect-[16/9] w-full bg-[#141414]" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-2/3 bg-[#141414] rounded" />
                  <div className="h-3 w-full bg-[#141414] rounded" />
                  <div className="h-3 w-5/6 bg-[#141414] rounded" />
                </div>
              </div>
            ))
          ) : useCases.map((uc, i) => {
            const hc = uc.resolved_hardware_class || {};
            const price = hc?.windows?.price || hc?.mac?.price || 0;
            const isPlaceholder = !!(hc?.windows?.is_placeholder || hc?.mac?.is_placeholder);
            return (
              <ProductCard
                key={uc.id}
                testId={`use-case-card-${uc.slug}`}
                image={uc.image}
                imageClassName="w-full h-full object-cover"
                iconUrl={uc.icon_url}
                icon={!uc.icon_url ? workstationIconForSlug(uc.slug) : null}
                name={uc.display_name}
                titleClassName="text-[26px] leading-[1.1] sm:text-[19px] sm:leading-tight"
                byline={uc.byline}
                description={uc.short_description}
                checklist={uc.features}
                checklistLabel={includesLabel}
                reveal={{ delay: i * 70 }}
                priceFormatted={`AWG ${formatAWG(price)}${isPlaceholder ? " *" : ""}`}
                priceSub={isPlaceholder ? "Pricing under review" : usdApprox(price)}
                primaryCta={{
                  label: addToCartLabel,
                  onClick: () => navigate(`/workstations/${uc.slug}`),
                  testId: `use-case-buy-${uc.slug}`,
                }}
              />
            );
          })}
        </div>
      </section>

      {/* Teams & Organizations B2B callout (Feb 2026 refinement).
          One integrated dark panel — background photograph spans the
          full panel and blends into the composition via a strong
          left-to-right #141414 gradient so the left ~55% remains
          fully legible while the photograph reads as atmosphere on
          the right. Reuses the same dark tokens (#141414 / #262626
          / rounded-2xl / orange CTA) as the workstation product
          cards above so the two rows feel like one design system.
          Mobile: image drops beneath the content; decorative
          right-side micro-copy hides. */}
      <section
        id="volume-laptops-section"
        data-testid="volume-laptops-section"
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24 sm:pb-28 scroll-mt-24"
      >
        <div
          className="relative overflow-hidden rounded-2xl border border-[#262626] bg-[#0F0F0F] max-w-[1200px] mx-auto lg:min-h-[440px]"
        >
          {/* Right-side photograph — occupies ~45% of the panel on
              desktop and dissolves into the dark content area via a
              strong horizontal gradient. Below lg the image is
              rendered inline BELOW the content block instead. */}
          <div
            data-testid="volume-laptops-visual"
            className="hidden lg:block absolute inset-y-0 right-0 w-[58%] z-0"
          >
            <img
              src={vImage}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            {/* Left-edge fade of the image into the dark content area.
                Anchored to the LEFT of this container (which starts
                at panel-left + 42%), so the fade band sits over the
                middle of the panel — reproducing the reference's
                seamless transition without a visible dividing line. */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F] via-[#0F0F0F]/50 to-transparent"
            />
          </div>

          {/* Content layer — left ~55–60% remains predominantly dark
              so headline / paragraph / benefits / CTA never sit on
              top of visible photographic detail. */}
          <div className="relative z-10 flex flex-col p-6 sm:p-10 lg:p-12 lg:pr-16 lg:max-w-[62%]">
            <div className="ur-eyebrow" data-testid="volume-laptops-eyebrow">{vEyebrow}</div>
            <h2
              data-testid="volume-laptops-heading"
              className="font-display mt-4 text-4xl sm:text-5xl lg:text-[52px] font-black leading-[1.02] tracking-tight text-white"
            >
              {vHeading && vHeading.endsWith(".") ? (
                <>
                  {vHeading.slice(0, -1)}
                  <span className="text-[#FF6600]">.</span>
                </>
              ) : (
                vHeading
              )}
            </h2>
            <p
              data-testid="volume-laptops-description"
              className="mt-5 text-[15px] text-[#B5B5B5] leading-relaxed max-w-[52ch]"
            >
              {vDescription}
            </p>

            {/* Benefits — tight horizontal 3-column cluster with thin
                #262626 vertical separators between groups. Icons are
                large orange line-glyphs sitting DIRECTLY on the dark
                panel — no boxes, no borders, no background plates.
                Constrained within the left/middle portion so they
                never stretch over the photograph on the right. */}
            <ul
              data-testid="volume-laptops-benefits"
              className="mt-8 lg:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-y-6 lg:gap-y-0 lg:divide-x lg:divide-[#262626]"
            >
              {[
                {
                  Icon: Users,
                  title: "Volume Pricing",
                  body: "Competitive pricing for multiple units.",
                  testId: "volume-benefit-pricing",
                },
                {
                  Icon: Cog,
                  title: "Professional Setup",
                  body: "Hardware, software and configuration ready to deploy.",
                  testId: "volume-benefit-setup",
                },
                {
                  Icon: Laptop,
                  title: "Hardware + Software Sourcing",
                  body: "We recommend and source the right solutions for your team.",
                  testId: "volume-benefit-sourcing",
                },
              ].map(({ Icon, title, body, testId }, i) => (
                <li
                  key={testId}
                  data-testid={testId}
                  className={`flex items-start gap-3.5 lg:flex-col lg:items-start lg:gap-3 ${i > 0 ? "lg:pl-5" : ""} ${i < 2 ? "lg:pr-5" : ""}`}
                >
                  <Icon
                    className="w-8 h-8 text-[#FF6600] shrink-0"
                    strokeWidth={1.9}
                  />
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white leading-snug">
                      {title}
                    </div>
                    <div className="mt-1.5 text-[13px] text-[#A3A3A3] leading-[1.5] max-w-[22ch]">
                      {body}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* CTA row — pill button on the left, uppercase caption
                immediately to its right (matches the reference). The
                caption hides on mobile so the CTA remains the clear
                focus at narrow widths. */}
            <div className="mt-9 lg:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <button
                type="button"
                onClick={() => setVolumeOpen(true)}
                data-testid="volume-laptops-cta"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF6600] hover:bg-[#FF8533] text-white text-sm font-bold uppercase tracking-widest transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                {vCtaLabel}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
              </button>
              {vSupportingLine && (
                <p
                  data-testid="volume-laptops-supporting-line"
                  className="hidden sm:block text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#7A7A7A] leading-[1.7] whitespace-pre-line"
                >
                  {vSupportingLine}
                </p>
              )}
            </div>
          </div>

          {/* Mobile-only image — appears BELOW the content per the
              Feb 2026 stacking spec. Intentional 16/9 crop so
              multiple laptops still read at narrow widths. */}
          <div
            data-testid="volume-laptops-visual-mobile"
            className="lg:hidden relative aspect-[16/9] w-full"
          >
            <img
              src={vImage}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-right"
            />
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#0F0F0F] to-transparent"
            />
          </div>

          {/* Decorative right-side micro-copy sitting on top of the
              photograph in its upper-right open area. Desktop-only. */}
          <div
            data-testid="volume-laptops-micro-copy"
            className="hidden lg:block absolute right-10 top-10 z-10 text-right pointer-events-none"
          >
            <div className="text-[13px] font-black uppercase tracking-[0.24em] text-white/95 leading-[1.55]">
              Same Tools.
              <br />
              Higher Impact
              <br />
              Together.
            </div>
            <div
              aria-hidden="true"
              className="mt-3 ml-auto h-[2px] w-12 bg-[#FF6600]"
            />
          </div>
        </div>
      </section>

      <VolumeLaptopInquiryModal open={volumeOpen} onClose={() => setVolumeOpen(false)} />
    </div>
  );
}
